/**
 * The tokens a user can trade (a perpetual on at least one of our futures
 * exchanges), built from the price service's `/v1/markets` list, for the
 * ticker picker.
 */

import { FUTURES_PRICE_PRIORITY, type FuturesServiceExchange } from "./exchanges";
import type { ServiceTokenList } from "./price-service-rest";

export interface TradableToken {
  /** `<BASE>USDT`. */
  symbol: string;
  /** `BTC`. */
  base: string;
  /** Futures exchanges listing it, in FUTURES_PRICE_PRIORITY order. */
  exchanges: FuturesServiceExchange[];
  /** Summed 24h futures quote volume (USDT), when the service reports one. */
  volume24h: number | null;
}

/**
 * Most traded first (24h volume), then listed on the most exchanges, then
 * alphabetical.
 */
export function buildTradableTokens(list: ServiceTokenList): TradableToken[] {
  const bySymbol = new Map<string, FuturesServiceExchange[]>();
  for (const exchange of FUTURES_PRICE_PRIORITY) {
    for (const symbol of list.exchanges[exchange] ?? []) {
      if (!symbol.endsWith("USDT") || symbol.length <= 4) continue;
      const venues = bySymbol.get(symbol) ?? [];
      if (!venues.includes(exchange)) venues.push(exchange);
      bySymbol.set(symbol, venues);
    }
  }

  const tokens: TradableToken[] = [];
  for (const [symbol, exchanges] of bySymbol) {
    tokens.push({
      symbol,
      base: symbol.slice(0, -4),
      exchanges,
      volume24h: list.volumes[symbol] ?? null,
    });
  }
  return tokens.sort(
    (a, b) =>
      (b.volume24h ?? -1) - (a.volume24h ?? -1) ||
      b.exchanges.length - a.exchanges.length ||
      a.base.localeCompare(b.base)
  );
}

/**
 * Tokens matching a search, keeping the list order: exact ticker first, then
 * tickers starting with the search, then tickers containing it.
 */
export function searchTradableTokens(
  tokens: TradableToken[],
  query: string
): TradableToken[] {
  const needle = query.trim().toUpperCase().replace(/[/\-_ ]/g, "");
  if (!needle) return tokens;
  const base = needle.endsWith("USDT") && needle.length > 4 ? needle.slice(0, -4) : needle;
  const exact: TradableToken[] = [];
  const prefix: TradableToken[] = [];
  const contains: TradableToken[] = [];
  for (const token of tokens) {
    if (token.base === base) exact.push(token);
    else if (token.base.startsWith(base)) prefix.push(token);
    else if (token.base.includes(base)) contains.push(token);
  }
  return [...exact, ...prefix, ...contains];
}
